import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users, ShieldCheck, ShieldAlert, Sparkles, ArrowRight,
    Globe, Wallet, Clock, Landmark,
} from "lucide-react";
import api from "@/lib/api";
import { fmt } from "@/lib/format";
import { Button } from "@/components/ui/button";

const StatCard = ({ icon: Icon, label, value, accent, testId, onClick }) => {
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`bg-white border border-slate-200 rounded-md p-5 transition-shadow text-left w-full ${
                onClick
                    ? "hover:shadow-md hover:border-slate-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-300"
                    : "hover:shadow-sm"
            }`}
            data-testid={testId}
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="overline mb-3">{label}</div>
                    <div className="font-display text-3xl font-bold tracking-tight text-slate-900">
                        {value}
                    </div>
                </div>
                <div
                    className="h-9 w-9 rounded-md flex items-center justify-center"
                    style={{ background: accent + "1A", color: accent }}
                >
                    <Icon size={18} strokeWidth={2} />
                </div>
            </div>
        </Tag>
    );
};

export default function Dashboard() {
    const [stats, setStats] = useState({
        total: 0, actifs: 0, inactifs: 0, mega: 0,
        factweb: 0, payable: 0, surveil: 0, depodirect: 0,
        nouveaux_30j: 0,
    });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/avocats/stats");
                setStats(data);
            } catch {
                /* ignore */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const v = (n) => loading ? "…" : fmt(n);

    // Navigation rapide depuis une card vers la grille filtrée.
    // /avocats lit `?mega=true` / `?statut=actif` au mount et applique le filtre.
    const go = (qs) => navigate(qs ? `/avocats?${qs}` : "/avocats");

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="overline mb-2">Tableau de bord</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
                        Vue d&apos;ensemble.
                    </h1>
                    <p className="text-sm text-slate-600 mt-2 max-w-lg">
                        Suivi en temps réel des avocats inscrits, statuts et activités récentes.
                    </p>
                </div>
                <Button
                    onClick={() => navigate("/avocats")}
                    className="rounded-md bg-[#0033A0] hover:bg-[#002277] text-white"
                    data-testid="dashboard-go-avocats"
                >
                    Voir tous les avocats <ArrowRight size={16} className="ml-2" />
                </Button>
            </div>

            {/* Bloc principal : totaux + ventilation Actifs/Inactifs */}
            <div>
                <div className="overline mb-3">Vue générale</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <StatCard icon={Users} label="Total avocats" value={v(stats.total)}
                        accent="#0033A0" testId="stat-total" onClick={() => go("")} />
                    <StatCard icon={ShieldCheck} label="Actifs" value={v(stats.actifs)}
                        accent="#16A34A" testId="stat-actifs" onClick={() => go("statut=actif")} />
                    <StatCard icon={ShieldAlert} label="Inactifs" value={v(stats.inactifs)}
                        accent="#DC2626" testId="stat-inactifs" onClick={() => go("statut=inactif")} />
                </div>
            </div>

            {/* Bloc statuts secondaires (toggles) */}
            <div>
                <div className="overline mb-3">Statuts</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <StatCard icon={Globe} label="Facturation web" value={v(stats.factweb)}
                        accent="#0284C7" testId="stat-factweb" onClick={() => go("factweb=true")} />
                    <StatCard icon={Wallet} label="Payables" value={v(stats.payable)}
                        accent="#059669" testId="stat-payable" onClick={() => go("payable=true")} />
                    <StatCard icon={Clock} label="En attente" value={v(stats.surveil)}
                        accent="#D97706" testId="stat-surveil" onClick={() => go("surveil=true")} />
                    <StatCard icon={Landmark} label="Dépôt direct" value={v(stats.depodirect)}
                        accent="#4F46E5" testId="stat-depodirect" onClick={() => go("depodirect=true")} />
                    <StatCard icon={Sparkles} label="Méga" value={v(stats.mega)}
                        accent="#7C3AED" testId="stat-mega" onClick={() => go("mega=true")} />
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-8">
                <div className="overline mb-2">Activité</div>
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-1">
                    {fmt(stats.nouveaux_30j)} {stats.nouveaux_30j > 1 ? "nouveaux avocats" : "nouvel avocat"}{" "}
                    sur les 30 derniers jours
                </h2>
                <p className="text-sm text-slate-600">
                    Les inscriptions récentes sont synchronisées en temps réel avec la base centrale.
                </p>
            </div>
        </div>
    );
}
